import { isFromAnotherExamsPaper, type Question } from './parser'
import { getPastExamSittings, type ExamSession, type PastExamSitting } from '@/data/pastExams'
import { paceForExam } from './quizTiming'

// The read side of the past-exam shelf: merges the authored sitting catalogue
// (`data/pastExams.ts`) with the sittings the question bank actually holds, and
// hands the browser one row per sitting — available or not.
//
// Pure and bank-driven on purpose: a newly converted paper appears in the
// browser as soon as its questions land, whether or not the catalogue lists it.

export interface PastExamRow {
  /** Stable identity for React keys and selection: `"2019|Spring"`. */
  key: string
  year: number
  /** Normalized session, absent for a sitting the bank tags with a year only. */
  session?: ExamSession
  /** Display name, e.g. `"Spring 2019"` — or just the year when untagged. */
  label: string
  /** Questions from this sitting in the app's bank. `0` = nothing imported yet. */
  bankCount: number
  /** Questions on the real paper, when the catalogue records it. */
  officialQuestionCount?: number
  candidates?: number
  passRate?: number
  effectivePassRate?: number
  /** Whether this sitting can actually be sat in the app. */
  available: boolean
}

/** Session strings in question frontmatter aren't consistent — `Sp`, `spring`, `Spring`. */
export function normalizeSession(session?: string | null): ExamSession | undefined {
  const s = session?.trim().toLowerCase()
  if (s === 'spring' || s === 'sp') return 'Spring'
  if (s === 'fall' || s === 'fa' || s === 'autumn') return 'Fall'
  return undefined
}

/** `"Spring 2019"`, or the bare year for a sitting with no session. */
export function sittingLabel(year: number, session?: string | null): string {
  const normalized = normalizeSession(session)
  return normalized ? `${normalized} ${year}` : String(year)
}

function rowKey(year: number, session?: ExamSession): string {
  return `${year}|${session ?? ''}`
}

/** Fall comes after Spring in the same year, so it sorts first newest-first. */
function sessionRank(session?: ExamSession): number {
  if (session === 'Fall') return 0
  if (session === 'Spring') return 1
  return 2
}

/**
 * The sitting label a question belongs to, or null when its file names none.
 *
 * Read off the question's own frontmatter, never inferred — the same rule
 * `lib/questionSource.ts` keeps. A question re-tagged onto another exam's
 * syllabus keeps the date of the paper it was actually sat on.
 */
export function questionSittingLabel(q: Pick<Question, 'year' | 'session'>): string | null {
  return q.year ? sittingLabel(q.year, q.session) : null
}

/**
 * The sittings a set of questions actually came from, newest first — the
 * options behind the question search's **Sitting** filter.
 *
 * Keyed by display label (`"Spring 2019"`), which is what a question is matched
 * back against. Undated questions contribute no option rather than a
 * catch-all: the filter offers the papers the pool holds, nothing more.
 */
export function sittingLabels(questions: Pick<Question, 'year' | 'session'>[]): string[] {
  const seen = new Map<string, { year: number; session?: ExamSession }>()
  for (const q of questions) {
    const label = questionSittingLabel(q)
    if (!label || !q.year) continue
    seen.set(label, { year: q.year, session: normalizeSession(q.session) })
  }
  return [...seen.entries()]
    .sort(([, a], [, b]) => (
      b.year !== a.year ? b.year - a.year : sessionRank(a.session) - sessionRank(b.session)
    ))
    .map(([label]) => label)
}

/**
 * A question's number on the paper it was sat on — `12` for `cas5-2019s-q12` —
 * or null when its id carries none (the undated SOA sample set).
 *
 * The converters name every dated question `<paper>-q<n>` after the number the
 * paper printed, so the id is where the paper's order is recorded. The gaps a
 * sitting shows (a question moved to another exam) are the paper's gaps too.
 */
export function paperQuestionNumber(id: string): number | null {
  const match = /-q(\d+)$/i.exec(id)
  return match ? Number(match[1]) : null
}

/**
 * Questions in the order the paper set them: question 1 first, compared as
 * numbers so Q10 follows Q9 rather than Q1.
 *
 * A past sitting is one paper, but a sitting the bank tags with a year only
 * matches every session of that year, so papers are kept apart first — oldest
 * first, Spring before Fall. A question with no number keeps its place behind
 * the numbered ones rather than being guessed into the run.
 */
export function inPaperOrder<T extends Pick<Question, 'id' | 'year' | 'session'>>(questions: readonly T[]): T[] {
  return [...questions].sort((a, b) => {
    if ((a.year ?? 0) !== (b.year ?? 0)) return (a.year ?? 0) - (b.year ?? 0)
    const sessionOrder = sessionRank(normalizeSession(b.session)) - sessionRank(normalizeSession(a.session))
    if (sessionOrder !== 0) return sessionOrder
    const aNumber = paperQuestionNumber(a.id)
    const bNumber = paperQuestionNumber(b.id)
    if (aNumber === null || bNumber === null) {
      return aNumber === null ? (bNumber === null ? 0 : 1) : -1
    }
    return aNumber - bNumber
  })
}

/**
 * One row per past sitting of `exam`, newest first.
 *
 * Rows come from the union of the authored catalogue and the sittings present
 * in `questions` — the catalogue contributes the papers that exist but haven't
 * been imported (rendered greyed out), the bank contributes anything imported
 * ahead of the catalogue.
 *
 * The bank half is deliberately not "every question tagged with a year": a
 * question re-tagged onto this exam from another one carries the date of the
 * paper it was actually sat on, so it names a sitting of that *other* exam
 * (`isFromAnotherExamsPaper`). Letting one build a row invents a paper nobody
 * ever sat; letting one join a real row pads that paper with questions it
 * never held.
 */
export function buildPastExamRows(questions: Question[], exam: string): PastExamRow[] {
  const rows = new Map<string, PastExamRow>()

  const add = (year: number, session: ExamSession | undefined, sitting?: PastExamSitting) => {
    const key = rowKey(year, session)
    const existing = rows.get(key)
    if (existing) {
      // A catalogue entry arriving after a bank-derived row fills in its stats.
      if (sitting) {
        existing.officialQuestionCount ??= sitting.officialQuestionCount
        existing.candidates ??= sitting.candidates
        existing.passRate ??= sitting.passRate
        existing.effectivePassRate ??= sitting.effectivePassRate
      }
      return
    }
    rows.set(key, {
      key,
      year,
      session,
      label: sittingLabel(year, session),
      bankCount: 0,
      officialQuestionCount: sitting?.officialQuestionCount,
      candidates: sitting?.candidates,
      passRate: sitting?.passRate,
      effectivePassRate: sitting?.effectivePassRate,
      available: false,
    })
  }

  for (const sitting of getPastExamSittings(exam)) {
    add(sitting.year, sitting.session, sitting)
  }

  for (const q of questions) {
    if (q.exam !== exam || !q.year) continue
    if (isFromAnotherExamsPaper(q, exam)) continue
    const session = normalizeSession(q.session)
    add(q.year, session)
    const row = rows.get(rowKey(q.year, session))!
    row.bankCount += 1
    row.available = true
  }

  return [...rows.values()].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year
    return sessionRank(a.session) - sessionRank(b.session)
  })
}

/**
 * What a session drawn from this source is called on screen.
 *
 * "Mock" says the paper is a fabrication, which is the one thing it usually
 * isn't: what the shelf hands you is a real released sitting, sat as practice.
 * The session is practice either way — a generated Mix and Fall 2019 alike —
 * so the mode carries one name and only the *source* below distinguishes them.
 */
/**
 * How many questions a practice exam draws on an exam whose paper is not paced
 * per question — the real paper's length where a source gives one, its
 * released papers' size where none does. An exam paced per question
 * (`EXAM_PACE` in lib/quizTiming.ts) takes its length from there instead, so
 * the timer and the paper can't disagree about how long the exam is.
 */
export const PRACTICE_EXAM_QUESTIONS: Readonly<Record<string, number>> = {
  // Guides/Exam 5 (CAS): "typically around 25 questions worth roughly 55
  // points" — the points are what a practice exam is drawn to
  // (lib/practiceExam.ts); this is the count the builder shows beside them.
  'Exam 5': 25,
  // The SOA's syllabi: ASTAM six written-answer questions (one of them the
  // Excel question the bank omits), and ALTAM the same.
  'Exam ALTAM': 6,
  'Exam ASTAM': 6,
  // No format guide gives these, so they are the released papers' own size:
  // the thirteen Fall 2013–Fall 2019 Exam 6C papers held 394 questions, the
  // eight 2012–2019 Exam 7 papers 200, the eight Exam 8 papers 173 and the
  // seven Spring 2013–2019 Exam 9 papers 172 (`cas6c-*` / `cas7-*` / `cas8-*` /
  // `cas9-*` across the bank, wherever the syllabus has since filed them).
  'Exam 6C': 30,
  'Exam 7': 25,
  'Exam 8': 22,
  'Exam 9': 25,
}

/**
 * A practice exam's length on `exam` (a bank label): the paper's question
 * count where its pace is per question, else the table above, 30 where
 * nothing says. Read by the quiz builder's Practice Exam and by Actuaria's
 * Simulation (docs/actuaria-online.md §6.10), so the two can't disagree.
 */
export function practiceExamQuestions(exam: string): number {
  const pace = paceForExam(exam)
  if (pace?.per === 'question') return pace.units
  return PRACTICE_EXAM_QUESTIONS[exam] ?? 30
}

export const PRACTICE_EXAM_LABEL = 'Practice Exam'

/**
 * What the source itself is called: the tab on the quiz builder, above the shelf.
 *
 * The label follows the content, the same rule the report button follows
 * (`Examiner's Report` vs `Exam & Answer Key`): an exam whose body released its
 * papers offers **Past Papers**, and one with none — Exam P and FM draw on the
 * SOA's rolling sample set, so their shelf is the generated Mix row and the
 * sample set's PDFs — offers **By Source**, the shelf's sources (the Mix and the
 * sample set) without claiming a paper. Naming the Mix "past papers" would
 * overstate it.
 *
 * It names a *source* of questions and nothing else. It used to fall back to
 * "Practice Exam", when the tab also decided how the questions were sat; that
 * is the builder's format choice now (Quiz or Practice Exam, beside Start), and
 * any source can be sat as either.
 */
export function examSourceLabel(rows: PastExamRow[]): string {
  return rows.length > 0 ? 'Past Papers' : 'By Source'
}

/** A percentage for display: `40` → `"40%"`, `40.6` → `"40.6%"`, nothing → `null`. */
export function formatPassRate(rate?: number): string | null {
  if (rate === undefined || Number.isNaN(rate)) return null
  return `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`
}

/** True when any row in the shelf carries published statistics worth a column. */
export function hasPublishedStats(rows: PastExamRow[]): boolean {
  return rows.some(r => r.effectivePassRate !== undefined || r.passRate !== undefined)
}
